import { useCallback, useRef, useState } from 'react';
import { z } from 'zod';

import type { SubscriptionId } from '../../api';
import { type ApiError, toApiError } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { useActionKeys } from '../../lib/api/idempotency-key';

/** Where the stop is. */
export type StopCopyingState =
  | { readonly status: 'idle' }
  | { readonly status: 'busy' }
  | { readonly status: 'failed'; readonly error: ApiError };

/**
 * `DELETE /subscriptions/{id}` for E7's "Stop copying this channel" (S9 R16): one request per
 * confirm, with an `Idempotency-Key` kept for a retry only after a network failure, a 5xx or
 * `IDEMPOTENCY_KEY_IN_PROGRESS` (S2 R13). A 404 `NOT_FOUND` means another device already removed
 * it: done, like a 204 (S9 R12). `onStopped` navigates away, so no state is set after it.
 */
export function useStopCopying(
  subscriptionId: SubscriptionId,
  onStopped: () => void,
): { readonly state: StopCopyingState; readonly run: () => void; readonly reset: () => void } {
  const api = useApiClient();
  const keys = useActionKeys('one');
  const inFlight = useRef(false);
  const [state, setState] = useState<StopCopyingState>({ status: 'idle' });

  const run = useCallback(() => {
    if (inFlight.current) return; // AC16: one confirm, one request
    inFlight.current = true;
    const action = `stop:${subscriptionId}`;
    const guard = { kind: 'authenticated', idempotencyKey: keys.keyFor(action) } as const;
    setState({ status: 'busy' });
    api.delete(`/subscriptions/${encodeURIComponent(subscriptionId)}`, { guard }, z.void()).then(
      () => {
        keys.settle(action, null);
        inFlight.current = false;
        onStopped();
      },
      (error: unknown) => {
        const failure = toApiError(error);
        keys.settle(action, failure);
        inFlight.current = false;
        if (failure.status === 404 && failure.reasonCode === 'NOT_FOUND') {
          onStopped();
          return;
        }
        setState({ status: 'failed', error: failure });
      },
    );
  }, [api, keys, onStopped, subscriptionId]);

  const reset = useCallback(() => {
    setState({ status: 'idle' });
  }, []);

  return { state, run, reset };
}
